import JWT from 'jsonwebtoken';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Route } from 'react-router-dom';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { GetEntitlements } from '../../FeatureSetup/ReducerActions/GetEntitlements';
import FullPageLoadingIndicator from '../LoadingIndicator/FullPageLoadingIndicator';
import { PLAN_MEMBER_ORIGINS, CLAIM_STEP, APP_CONSTANTS } from '../../utils/AppConstants';
import Timeout from '../Timeout/Timeout';
import { Authenticate } from './ReducerActions/Authenticate';
import { GetAEMContent } from './ReducerActions/GetAEMContent';
import { GetFamily } from './ReducerActions/GetFamily';
import { GetMember } from './ReducerActions/GetMember';
import { GetMemberBenefits } from './ReducerActions/GetMemberBenefits';
import { GetMemberSupplemental } from './ReducerActions/GetMemberSupplemental';
import { decodeCtx } from '../../utils/auth/ctxHelper';
import { StoreContext } from './ReducerActions/StoreContext';

/**
 * This function attached Adobe Analytics script to the document. It is purposefully done AFTER the critical path
 * requests have been sent.
 */
const AttachAdobeAnalytics = () => {
    const { ADOBE_ANALYTICS_URL } = window.__ENV__;
    const script = document.createElement('script');

    script.src = ADOBE_ANALYTICS_URL;
    script.async = true;

    document.body.appendChild(script);
};

const ProtectedRoute = ({ children, logger, path }) => {
    const {
        isLoadingFeatures,
        isLoadingMemberData,
        isLoadingAEMContent,
        isAuthenticated,
        jwt,
        sessionCorrelationId,
        isCsr,
    } = useShallowEqualSelector((state) => ({
        isLoadingFeatures: state.Features.isLoading,
        isLoadingMemberData: state.MemberData.isLoadingMemberData,
        isLoadingAEMContent: state.AEMContent.isLoading,
        isAuthenticated: state.Authentication.isAuthenticated,
        jwt: state.Authentication.jwt,
        sessionCorrelationId: state.Authentication.sessionCorrelationId,
        isCsr: state.Authentication.isCsr,
    }));

    const dispatch = useDispatch();

    useEffect(() => {
        if (!isAuthenticated) {
            dispatch(Authenticate());
        }
    }, [dispatch, isAuthenticated]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const origin = params.get('origin');
        const claimStep = params.get('claimstep');
        const ctxQueryString = params.get('ctx');
        // Ctx is not expected to be changed,
        // store in session storage and state for persistence and frequent access
        if (ctxQueryString) {
            sessionStorage.setItem(APP_CONSTANTS.MPROFILE_CTX, ctxQueryString);
        }
        dispatch(StoreContext(logger));

        if (PLAN_MEMBER_ORIGINS.includes(origin)) {
            sessionStorage.setItem(APP_CONSTANTS.MPROFILE_ORIGIN, origin);
        }
        if (CLAIM_STEP.includes(claimStep)) {
            sessionStorage.setItem(APP_CONSTANTS.MPROFILE_CLAIMSTEP, claimStep);
        }
    }, []);

    // When a new JWT is created, save it to session storage so it can be referenced and reused on refreshes.
    // Also, set the rest of the context for the logger now that we have all of the information.
    useEffect(() => {
        if (jwt) {
            sessionStorage.setItem(APP_CONSTANTS.MPROFILE_JWT, jwt);
            sessionStorage.setItem(APP_CONSTANTS.MPROFILE_SESSION_CORRELATION_ID, sessionCorrelationId);

            const jwtData = JWT.decode(jwt);
            logger.setContext({
                correlation: {
                    sessionId: sessionCorrelationId,
                },
                customer: {
                    group: jwtData.Identity.clientNumber,
                    cert: jwtData.Identity.certificate,
                    isCsr,
                },
            });
        }
    }, [jwt, sessionCorrelationId, isCsr, logger]);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(GetMember());
            dispatch(GetMemberBenefits());
            dispatch(GetFamily());
            dispatch(GetEntitlements());
            dispatch(GetAEMContent());
            dispatch(GetMemberSupplemental());
            AttachAdobeAnalytics();
        }
    }, [dispatch, isAuthenticated, logger]);

    if (isLoadingFeatures || isLoadingMemberData || isLoadingAEMContent || !isAuthenticated) {
        // Show loading if ANY data is still fetching
        return (
            <Timeout>
                <Route>
                    <FullPageLoadingIndicator />
                </Route>
            </Timeout>
        );
    }

    return (
        <Timeout>
            <Route path={path}>{children}</Route>
        </Timeout>
    );
};

export default ProtectedRoute;
